import React, { useState, useEffect, useMemo } from 'react';
import { PageSkeleton } from '../DashboardLoader';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Star,
  Image as ImageIcon,
  Utensils
} from 'lucide-react';
import {
  getRestaurantDishes,
  getRestaurantMenus,
  getRestaurantMenusWithDishes,
  createDish,
  createMenu,
  updateDish,
  updateMenu,
  deleteDish,
  deleteMenu,
  formatCurrency,
  handleRestaurantApiError,
  getCategories,
  getAllSupplements,
  getDishSupplements,
  createSupplement,
  updateSupplement,
  removeDishSupplement,
  getReviews,
  respondToReview
} from '../../../services/restaurantApi';
import SkeletonImage from '../../skeletons/SkeletonImage';
import { toastSuccess, toastError } from '../../../utils/toast';
import DishModal from '../modals/DishModal';
import MenuModal from '../modals/MenuModal';
import MenuDetailsModal from '../modals/MenuDetailsModal';
import SupplementModal from '../modals/SupplementModal';
import { RatingStarsFa } from '../../../utils/ratingStars';

const RestaurantMenu = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('dishes'); // 'dishes', 'menus', 'supplements' ou 'reviews'
  const [showAddDishModal, setShowAddDishModal] = useState(false);
  const [showAddMenuModal, setShowAddMenuModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [selectedDish, setSelectedDish] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [showMenuDetails, setShowMenuDetails] = useState(false);
  const [dishes, setDishes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredDishes, setFilteredDishes] = useState([]);
  const [filteredMenus, setFilteredMenus] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [filteredSupplements, setFilteredSupplements] = useState([]);
  const [showAddSupplementModal, setShowAddSupplementModal] = useState(false);
  const [selectedSupplement, setSelectedSupplement] = useState(null);
  const [selectedDishForSupplement, setSelectedDishForSupplement] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [reviewRatingFilter, setReviewRatingFilter] = useState('all');
  const [reviewReplyFilter, setReviewReplyFilter] = useState('all');
  const [reviewSort, setReviewSort] = useState('date_desc');
  const [reviewDishFilter, setReviewDishFilter] = useState('all');
  const [selectedDishIds, setSelectedDishIds] = useState({});
  const [bulkPublishing, setBulkPublishing] = useState(false);

  const reviewDishNames = useMemo(() => {
    const names = new Set();
    (reviews || []).forEach((r) => {
      const d = r?.dish_name?.trim();
      if (d) names.add(d);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [reviews]);

  // Fonction pour charger les données
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Charger les catégories
      const categoriesData = await getCategories();
      // S'assurer que categoriesData est un tableau d'objets valides
      const validCategories = Array.isArray(categoriesData)
        ? categoriesData.filter(cat => cat && typeof cat === 'object' && cat.id && cat.name)
        : [];
      setCategories(validCategories);

      // Charger les plats
      const dishesData = await getRestaurantDishes();
      console.log('Raw dishes data:', dishesData);
      // S'assurer que dishesData est un tableau d'objets valides
      const validDishes = Array.isArray(dishesData)
        ? dishesData.filter(dish => {
          const isValid = dish && typeof dish === 'object' && dish.id && dish.name;
          if (!isValid) {
            console.warn('Invalid dish filtered out:', dish);
          }
          return isValid;
        })
        : [];
      console.log('Valid dishes:', validDishes.length);
      setDishes(validDishes);

      // Charger les menus avec leurs plats
      console.log('Calling getRestaurantMenusWithDishes...');
      const menusData = await getRestaurantMenusWithDishes();
      console.log('Raw menus data:', menusData);
      console.log('First menu structure:', menusData?.[0] ? Object.keys(menusData[0]) : 'no data');
      if (menusData?.[0]?.dishes) {
        console.log('First menu dishes:', menusData[0].dishes.length, 'dishes');
      }
      // S'assurer que menusData est un tableau d'objets valides
      const validMenus = Array.isArray(menusData)
        ? menusData.filter(menu => {
          const isValid = menu && typeof menu === 'object' && menu.id && menu.name;
          if (!isValid) {
            console.warn('Invalid menu filtered out:', menu);
          }
          return isValid;
        })
        : [];
      console.log('Valid menus:', validMenus.length);
      setMenus(validMenus);

      // Charger les suppléments du restaurant
      try {
        const supplementsData = await getAllSupplements();
        const validSupplements = Array.isArray(supplementsData)
          ? supplementsData.filter(sup => sup && typeof sup === 'object' && sup.id)
          : [];
        setSupplements(validSupplements);
      } catch (supplementError) {
        console.error('Erreur lors du chargement des suppléments:', supplementError);
        setSupplements([]);
      }

      // Charger les avis du restaurant
      try {
        const reviewsData = await getReviews();
        console.log('📝 Avis reçus:', reviewsData);
        const validReviews = Array.isArray(reviewsData)
          ? reviewsData.filter(review => review && typeof review === 'object' && review.id)
          : [];
        console.log('📝 Avis valides:', validReviews.length);
        setReviews(validReviews);
        setFilteredReviews(validReviews);
      } catch (reviewError) {
        console.error('Erreur lors du chargement des avis:', reviewError);
        setReviews([]);
        setFilteredReviews([]);
      }

    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      setError('Erreur lors du chargement des données');
      handleRestaurantApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filtrer les plats, menus et suppléments
  useEffect(() => {
    console.log('Filtering data - activeTab:', activeTab, 'searchTerm:', searchTerm, 'filterCategory:', filterCategory);

    if (activeTab === 'supplements') {
      let filtered = [...supplements];
      
      if (searchTerm) {
        filtered = filtered.filter(sup => 
          sup.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          sup.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      if (filterCategory !== 'all') {
        // Filtrer par plat si un plat est sélectionné
        filtered = filtered.filter(sup => sup.dish_id === parseInt(filterCategory));
      }
      
      setFilteredSupplements(filtered);
    } else if (activeTab === 'reviews') {
      let filtered = Array.isArray(reviews) ? [...reviews] : [];

      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        filtered = filtered.filter((review) =>
          review.comment?.toLowerCase().includes(q) ||
          review.content?.toLowerCase().includes(q) ||
          review.user?.email?.toLowerCase().includes(q) ||
          review.user?.name?.toLowerCase().includes(q) ||
          review.user_name?.toLowerCase().includes(q) ||
          review.dish_name?.toLowerCase().includes(q)
        );
      }

      if (reviewRatingFilter !== 'all') {
        const want = Number(reviewRatingFilter);
        filtered = filtered.filter((review) => {
          const r = Math.round(Number(review.rating ?? review.score ?? 0));
          return r === want;
        });
      }

      if (reviewReplyFilter === 'with_reply') {
        filtered = filtered.filter((review) => review.reply && String(review.reply).trim());
      } else if (reviewReplyFilter === 'without_reply') {
        filtered = filtered.filter((review) => !review.reply || !String(review.reply).trim());
      }

      if (reviewDishFilter !== 'all') {
        filtered = filtered.filter(
          (review) => (review.dish_name || '').trim() === reviewDishFilter
        );
      }

      filtered.sort((a, b) => {
        const ra = Number(a.rating ?? a.score ?? 0);
        const rb = Number(b.rating ?? b.score ?? 0);
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        switch (reviewSort) {
          case 'date_asc':
            return da - db;
          case 'rating_desc':
            return rb - ra;
          case 'rating_asc':
            return ra - rb;
          case 'date_desc':
          default:
            return db - da;
        }
      });

      setFilteredReviews(filtered);
    } else if (activeTab === 'dishes') {
      const filtered = Array.isArray(dishes) ? dishes.filter(item => {
        if (!item || typeof item !== 'object') {
          console.warn('Invalid dish item in filter:', item);
          return false;
        }
        const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             item.description?.toLowerCase().includes(searchTerm.toLowerCase());
        // Gérer le cas où category est un objet ou un ID
        let categoryId = null;
        if (item.category) {
          if (typeof item.category === 'object' && item.category.id) {
            categoryId = item.category.id;
          } else if (typeof item.category === 'number') {
            categoryId = item.category;
          } else if (typeof item.category_id === 'number') {
            categoryId = item.category_id;
          }
        } else if (item.category_id) {
          categoryId = item.category_id;
        }
        const matchesCategory = filterCategory === 'all' || (categoryId && categoryId.toString() === filterCategory.toString());
        const notGrocery = !item.is_grocery_product;
        return matchesSearch && matchesCategory && notGrocery;
      }) : [];
      console.log('Filtered dishes:', filtered.length);
      setFilteredDishes(filtered);
    } else {
      const filtered = Array.isArray(menus) ? menus.filter(item => {
        if (!item || typeof item !== 'object') {
          console.warn('Invalid menu item in filter:', item);
          return false;
        }
        const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             item.description?.toLowerCase().includes(searchTerm.toLowerCase());
        // Gérer le cas où category est un objet ou un ID
        let categoryId = null;
        if (item.category) {
          if (typeof item.category === 'object' && item.category.id) {
            categoryId = item.category.id;
          } else if (typeof item.category === 'number') {
            categoryId = item.category;
          } else if (typeof item.category_id === 'number') {
            categoryId = item.category_id;
          }
        } else if (item.category_id) {
          categoryId = item.category_id;
        }
        const matchesCategory = filterCategory === 'all' || (categoryId && categoryId.toString() === filterCategory.toString());
        return matchesSearch && matchesCategory;
      }) : [];
      console.log('Filtered menus:', filtered.length);
      setFilteredMenus(filtered);
    }
  }, [
    dishes,
    menus,
    supplements,
    reviews,
    searchTerm,
    filterCategory,
    activeTab,
    reviewRatingFilter,
    reviewReplyFilter,
    reviewSort,
    reviewDishFilter
  ]);

  const handleCreateDish = async (dishData) => {
    try {
      // Préparer les données pour l'API backend
      const apiDishData = {
        name: dishData.name,
        description: dishData.description,
        price: dishData.price,
        category_id: dishData.category_id,
        dish_image_url: dishData.image_url || 'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=400&h=300&fit=crop',
        is_available: dishData.is_available !== false,
        hidden_from_marketplace: !!dishData.hidden_from_marketplace,
        is_grocery_product: !!dishData.is_grocery_product,
        catering_only: !!dishData.catering_only,
      };

      console.log('Sending dish data to API:', apiDishData);
      const newDish = await createDish(apiDishData);
      setDishes([...dishes, newDish]);
      setShowAddDishModal(false);
      toastSuccess('Plat créé avec succès.');
    } catch (err) {
      console.error('Erreur lors de la création du plat:', err);
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleUpdateDish = async (dishId, dishData) => {
    try {
      // Préparer les données pour l'API backend (même format que pour la création)
      const apiDishData = {
        name: dishData.name,
        description: dishData.description,
        price: dishData.price,
        category_id: dishData.category_id,
        dish_image_url: dishData.image_url || 'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=400&h=300&fit=crop',
        is_available: dishData.is_available !== false,
        hidden_from_marketplace: !!dishData.hidden_from_marketplace,
        is_grocery_product: !!dishData.is_grocery_product,
        catering_only: !!dishData.catering_only,
      };

      console.log('Updating dish with data:', apiDishData);
      const updatedDish = await updateDish(dishId, apiDishData);
      setDishes(dishes.map(dish => dish.id === dishId ? updatedDish : dish));
      setSelectedDish(null);
      toastSuccess('Plat mis à jour avec succès.');
    } catch (err) {
      console.error('Erreur lors de la mise à jour du plat:', err);
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleDeleteDish = async (dishId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce plat ?')) {
      try {
        await deleteDish(dishId);
        setDishes(dishes.filter(dish => dish.id !== dishId));
        toastSuccess('Plat supprimé.');
      } catch (err) {
        console.error('Erreur lors de la suppression du plat:', err);
        toastError(handleRestaurantApiError(err));
      }
    }
  };

  const toggleDishAvailability = (dish) => {
    handleUpdateDish(dish.id, {
      ...dish,
      image_url: dish.image_url || dish.dish_image_url,
      category_id: dish.category_id ?? (typeof dish.category === 'object' ? dish.category?.id : undefined),
      is_available: !(dish.is_available !== false),
    });
  };

  const toggleDishSelection = (dishId) => {
    setSelectedDishIds((prev) => ({ ...prev, [dishId]: !prev[dishId] }));
  };

  const selectedDishCount = Object.values(selectedDishIds).filter(Boolean).length;

  const handleBulkPublish = async () => {
    const ids = Object.keys(selectedDishIds).filter((id) => selectedDishIds[id]);
    if (ids.length === 0) return;
    setBulkPublishing(true);
    try {
      await Promise.all(
        ids.map((id) => {
          const dish = dishes.find((d) => String(d.id) === String(id));
          if (!dish || dish.is_available !== false) return Promise.resolve();
          return updateDish(dish.id, {
            name: dish.name,
            description: dish.description,
            price: dish.price,
            category_id: dish.category_id ?? (typeof dish.category === 'object' ? dish.category?.id : undefined),
            dish_image_url: dish.image_url || dish.dish_image_url,
            is_available: true,
            hidden_from_marketplace: !!dish.hidden_from_marketplace,
            is_grocery_product: !!dish.is_grocery_product,
            catering_only: !!dish.catering_only,
          });
        })
      );
      await loadData();
      setSelectedDishIds({});
      toastSuccess(`${ids.length} plat${ids.length > 1 ? 's' : ''} publié${ids.length > 1 ? 's' : ''}.`);
    } catch (err) {
      console.error('Erreur lors de la publication groupée:', err);
      toastError(handleRestaurantApiError(err));
    } finally {
      setBulkPublishing(false);
    }
  };

  // Handlers pour les suppléments
  const handleCreateSupplement = async (supplementData) => {
    try {
      const newSupplement = await createSupplement(supplementData);
      setSupplements([...supplements, newSupplement]);
      setShowAddSupplementModal(false);
      setSelectedDishForSupplement(null);
      loadData();
      toastSuccess('Supplément créé avec succès.');
    } catch (err) {
      console.error('Erreur lors de la création du supplément:', err);
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleUpdateSupplement = async (supplementId, supplementData) => {
    try {
      const updatedSupplement = await updateSupplement(supplementId, supplementData);
      setSupplements(supplements.map(sup => sup.id === supplementId ? updatedSupplement : sup));
      setSelectedSupplement(null);
      loadData();
      toastSuccess('Supplément mis à jour avec succès.');
    } catch (err) {
      console.error('Erreur lors de la mise à jour du supplément:', err);
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleDeleteSupplement = async (supplementId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce supplément ?')) {
      try {
        await removeDishSupplement(null, supplementId);
        setSupplements(supplements.filter(sup => sup.id !== supplementId));
        loadData();
        toastSuccess('Supplément supprimé.');
      } catch (err) {
        console.error('Erreur lors de la suppression du supplément:', err);
        toastError(handleRestaurantApiError(err));
      }
    }
  };

  const handleCreateMenu = async (menuData) => {
    try {
      const newMenu = await createMenu(menuData);
      setMenus([...menus, newMenu]);
      setShowAddMenuModal(false);
      toastSuccess('Menu créé avec succès.');
    } catch (err) {
      console.error('Erreur lors de la création du menu:', err);
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleUpdateMenu = async (menuId, menuData) => {
    try {
      await updateMenu(menuId, menuData);
      setEditingMenu(null);
      setShowAddMenuModal(false);
      await loadData(); // Recharger pour avoir image_url et plats à jour
      toastSuccess('Menu mis à jour avec succès.');
    } catch (err) {
      console.error('Erreur lors de la mise à jour du menu:', err);
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleDeleteMenu = async (menuId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce menu ?')) {
      try {
        await deleteMenu(menuId);
        setMenus(menus.filter(menu => menu.id !== menuId));
        toastSuccess('Menu supprimé.');
      } catch (err) {
        console.error('Erreur lors de la suppression du menu:', err);
        toastError(handleRestaurantApiError(err));
      }
    }
  };

  const renderDishCard = (dish, index) => {
    if (!dish || typeof dish !== 'object' || !dish.id || !dish.name) {
      console.warn('Invalid dish object:', dish);
      return (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="text-red-700 text-sm">Plat invalide</p>
        </div>
      );
    }

    const dishImg = dish.image_url || dish.dish_image_url;
    const categoryName = typeof dish.category === 'object' && dish.category?.name
      ? dish.category.name
      : (dish.category || 'Non catégorisé');
    const available = dish.is_available !== false;
    const selected = !!selectedDishIds[dish.id];

    return (
      <div
        key={dish.id}
        style={{
          display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', borderRadius: '26px', padding: '14px',
          border: selected ? '2px solid #054625' : '1px solid rgba(5,16,4,0.1)',
          background: selected ? '#FBF9D9' : 'transparent',
        }}
      >
        <button
          type="button"
          onClick={() => toggleDishSelection(dish.id)}
          aria-label={`Sélectionner ${dish.name}`}
          style={{
            flex: 'none', cursor: 'pointer', width: '26px', height: '26px', borderRadius: '9px', display: 'grid', placeItems: 'center', fontSize: '14px', fontWeight: 700,
            background: selected ? '#054625' : 'transparent', color: selected ? '#FBF9D9' : 'transparent',
            border: selected ? 'none' : '2px solid rgba(5,16,4,0.2)',
          }}
        >
          ✓
        </button>

        <span style={{ flex: 'none', width: '68px', height: '68px', borderRadius: '18px', overflow: 'hidden', display: 'block', position: 'relative', background: '#F4F1E2' }}>
          {dishImg ? (
            <SkeletonImage src={dishImg} alt={dish.name} width="w-full" height="h-full" className="rounded-none" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <ImageIcon className="h-6 w-6 text-[#054625]/25" aria-hidden />
            </div>
          )}
          {!available && (
            <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
              <span style={{ background: 'rgba(255,255,255,0.92)', fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '999px' }}>Masqué</span>
            </div>
          )}
        </span>

        <span style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span className="flex items-baseline gap-2.5 flex-wrap">
            <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '17px' }}>{dish.name}</strong>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', background: '#FBF9D9', padding: '4px 11px', borderRadius: '999px', whiteSpace: 'nowrap' }}>{categoryName}</span>
            {dish.catering_only && <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', background: '#F1F8E8', color: '#37702C', padding: '4px 11px', borderRadius: '999px', whiteSpace: 'nowrap' }}>Catering</span>}
            {dish.is_grocery_product && <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', background: '#FEF0EA', color: '#A32C0F', padding: '4px 11px', borderRadius: '999px', whiteSpace: 'nowrap' }}>Épicerie</span>}
          </span>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.68)' }} className="truncate">
            {dish.description || 'Sans description'}
          </span>
        </span>

        <strong style={{ fontSize: '16px', whiteSpace: 'nowrap', minWidth: '72px', textAlign: 'right' }}>{formatCurrency(dish.price)}</strong>

        <span className="flex items-center gap-2.5" style={{ whiteSpace: 'nowrap' }}>
          <button
            type="button"
            onClick={() => toggleDishAvailability(dish)}
            aria-pressed={available}
            style={{
              border: 'none', cursor: 'pointer', width: '50px', height: '28px', borderRadius: '999px', padding: '3px', display: 'flex',
              background: available ? '#72B744' : 'rgba(5,16,4,0.22)', transition: 'background 180ms ease',
            }}
          >
            <span style={{ width: '22px', height: '22px', borderRadius: '999px', background: '#FCFBF5', transform: available ? 'translateX(22px)' : 'translateX(0)', transition: 'transform 180ms ease' }} />
          </button>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, minWidth: '78px' }}>{available ? 'Disponible' : 'Masqué'}</span>
        </span>

        <span className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => { setSelectedDishForSupplement(dish); setShowAddSupplementModal(true); }}
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#FBF9D9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            title="Ajouter un supplément"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setSelectedDish(dish)}
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#FBF9D9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            title="Modifier"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => handleDeleteDish(dish.id)}
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#FEF0EA'; e.currentTarget.style.color = '#A32C0F'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(5,16,4,0.6)'; }}
            title="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </span>
      </div>
    );
  };

  const renderMenuCard = (menu, index) => {
    if (!menu || typeof menu !== 'object' || !menu.id || !menu.name) {
      console.warn('Invalid menu object:', menu);
      return (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="text-red-700 text-sm">Menu invalide</p>
        </div>
      );
    }

    if (!menu.dishes) {
      menu.dishes = [];
    } else if (!Array.isArray(menu.dishes)) {
      console.warn('menu.dishes is not an array:', menu.dishes);
      menu.dishes = [];
    }

    console.log(`Rendering menu ${menu.name} with ${menu.dishes.length} dishes`);

    const menuImg = menu.image_url || menu.dish_image_url;

    return (
      <div
        key={menu.id}
        className="group relative bg-[#FCFBF5] rounded-2xl overflow-hidden shadow-[0_2px_16px_rgba(5,70,37,0.06)] hover:shadow-[0_8px_32px_rgba(5,70,37,0.12)] transition-all duration-300 hover:-translate-y-0.5 flex flex-col cursor-pointer"
        onClick={() => { setSelectedMenu(menu); setShowMenuDetails(true); }}
      >
        <div className="relative h-44 overflow-hidden bg-gray-50 shrink-0">
          {menuImg ? (
            <div className="absolute inset-0">
              <SkeletonImage src={menuImg} alt={menu.name} width="w-full" height="h-full" className="rounded-none" />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#fff4ec] to-[#fde8db]">
              <Utensils className="h-12 w-12 text-[#EF4C23]/25" aria-hidden />
            </div>
          )}
          <div className="absolute top-3 right-3 z-10 flex gap-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100 transition-opacity duration-200">
            <button
              onClick={(e) => { e.stopPropagation(); setEditingMenu(menu); setShowAddMenuModal(true); }}
              className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-[#054625] hover:text-white transition-colors"
              title="Modifier le menu"
            >
              <Edit className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleDeleteMenu(menu.id); }}
              className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-red-500 hover:text-white transition-colors"
              title="Supprimer le menu"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="absolute bottom-3 left-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#EF4C23] text-white text-sm font-semibold shadow-lg">
              <Utensils className="h-3.5 w-3.5" />
              {menu.dishes.length} plat{menu.dishes.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <div className="p-4 flex flex-col flex-1">
          <h3 className="font-semibold text-namke-black text-base leading-snug mb-1.5">{menu.name}</h3>
          <p className="text-sm text-namke-black font-normal line-clamp-2 flex-1 leading-relaxed">{menu.description}</p>
          {Array.isArray(menu.dishes) && menu.dishes.length > 0 && (
            <div className="mt-3 pt-3 border-t border-black/[0.04] flex flex-wrap gap-1.5">
              {menu.dishes.slice(0, 3).map((dish, i) => {
                if (!dish || typeof dish !== 'object' || !dish.id || !dish.name) return null;
                return (
                  <span key={dish.id} className="text-xs px-2.5 py-0.5 rounded-full bg-[#054625]/10 text-[#054625] font-medium">
                    {dish.name}
                  </span>
                );
              })}
              {menu.dishes.length > 3 && (
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-gray-100 text-namke-black/45 font-medium">
                  +{menu.dishes.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSupplementCard = (supplement, index) => {
    if (!supplement || typeof supplement !== 'object' || !supplement.id || !supplement.name) {
      console.warn('Invalid supplement object:', supplement);
      return (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="text-red-700 text-sm">Supplément invalide</p>
        </div>
      );
    }

    const associatedDish = dishes.find(d => d.id === supplement.dish_id);

    return (
      <div key={supplement.id} className="group bg-[#FCFBF5] rounded-2xl p-5 shadow-[0_2px_16px_rgba(5,70,37,0.06)] hover:shadow-[0_8px_32px_rgba(5,70,37,0.10)] transition-all duration-300 hover:-translate-y-0.5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h3 className="font-semibold text-namke-black text-base leading-snug">{supplement.name}</h3>
              <span className="shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full bg-[#054625] text-white text-xs font-semibold">
                {formatCurrency(supplement.price || 0)}
              </span>
            </div>
            <p className="text-sm text-namke-black font-normal line-clamp-2 leading-relaxed mb-3">{supplement.description || 'Aucune description'}</p>
            {associatedDish && (
              <div className="flex items-center gap-1.5">
                <Utensils className="h-3.5 w-3.5 text-[#72B744] shrink-0" />
                <span className="text-xs text-namke-black font-normal truncate">{associatedDish.name}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100 transition-opacity duration-200">
            <button
              onClick={() => setSelectedSupplement(supplement)}
              className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm hover:bg-[#054625] hover:text-white transition-colors"
            >
              <Edit className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => handleDeleteSupplement(supplement.id)}
              className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm hover:bg-red-500 hover:text-white transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderReviewCard = (review, index) => {
    if (!review || typeof review !== 'object' || !review.id) {
      console.warn('Invalid review object:', review);
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">Avis invalide</p>
        </div>
      );
    }

    return (
      <div key={review.id} className="px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <RatingStarsFa rating={review.rating ?? review.score ?? 0} size="sm" />
              <span className="text-sm font-medium text-namke-black/70">
                {review.user?.name || review.user?.email || review.user_name || 'Utilisateur anonyme'}
              </span>
              {review.dish_name && (
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-[#054625]/10 text-[#054625] font-medium">{review.dish_name}</span>
              )}
              {review.created_at && (
                <span className="text-xs text-namke-black font-normal ml-auto">{new Date(review.created_at).toLocaleDateString('fr-FR')}</span>
              )}
            </div>
            <p className="text-sm text-namke-black leading-relaxed">{review.comment || review.content || 'Aucun commentaire'}</p>
            {review.reply && (
              <div className="mt-3 pl-4 border-l-2 border-[#054625]/30 bg-[#054625]/[0.03] rounded-r-lg py-2 pr-3">
                <p className="text-xs text-[#054625] font-medium mb-1">Réponse du restaurant</p>
                <p className="text-sm text-namke-black">{review.reply}</p>
              </div>
            )}
          </div>
          {!review.reply && (
            <button
              onClick={() => {
                const reply = window.prompt('Répondre à cet avis :');
                if (reply) {
                  respondToReview(review.id, { reply }).then(() => {
                    loadData();
                  }).catch(err => {
                    console.error('Erreur lors de la réponse:', err);
                    handleRestaurantApiError(err);
                  });
                }
              }}
              className="shrink-0 px-3.5 py-1.5 text-sm bg-[#054625] text-white rounded-xl hover:bg-[#033818] transition-colors font-medium"
            >
              Répondre
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) return <PageSkeleton />;

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vw, 24px)' }}>
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#EF4C23' }}>
            {dishes.length} plats · {categories.length} catégories
          </span>
          <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3vw, 38px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Ma carte</h1>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <button
            onClick={() => setShowAddDishModal(true)}
            className="inline-flex items-center gap-2"
            style={{ border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 20px', borderRadius: '999px' }}
          >
            <Plus size={16} />
            Nouveau plat
          </button>
          <button
            onClick={() => setShowAddMenuModal(true)}
            className="inline-flex items-center gap-2"
            style={{ border: 'none', cursor: 'pointer', background: '#054625', color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 20px', borderRadius: '999px' }}
          >
            <Plus size={16} />
            Nouveau menu
          </button>
          <button
            onClick={() => setShowAddSupplementModal(true)}
            type="button"
            className="inline-flex items-center gap-2"
            style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: '#054625', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 20px', borderRadius: '999px' }}
          >
            <Plus size={16} />
            Supplément
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex flex-wrap gap-1" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '4px', width: 'fit-content' }}>
        {[
          { key: 'dishes', label: 'Plats', count: dishes.length },
          { key: 'menus', label: 'Menus', count: menus.length },
          { key: 'supplements', label: 'Suppléments', count: supplements.length },
          { key: 'reviews', label: 'Avis', count: reviews.length },
        ].map(tab => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2"
              style={{
                border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontSize: '13.5px', fontWeight: 600,
                padding: '9px 16px', borderRadius: '999px', whiteSpace: 'nowrap',
                background: active ? '#054625' : 'transparent',
                color: active ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
              }}
            >
              {tab.label}
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 700, color: active ? '#FFC717' : 'rgba(5,16,4,0.4)' }}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filtres et recherche */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <label className="flex items-center gap-2.5" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '0 18px', minHeight: '44px' }}>
            <Search size={15} style={{ color: '#054625', flexShrink: 0 }} />
            <input
              type="text"
              placeholder={
                activeTab === 'reviews'
                  ? 'Rechercher (commentaire, client, plat…)'
                  : 'Rechercher...'
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '14px', color: '#051004', outline: 'none' }}
            />
          </label>
        </div>
        {activeTab === 'reviews' ? (
          <div className="flex flex-wrap gap-2 w-full sm:flex-1 sm:min-w-0 sm:justify-end">
            <select
              value={reviewRatingFilter}
              onChange={(e) => setReviewRatingFilter(e.target.value)}
              className="min-w-[8.5rem] flex-1 sm:flex-initial"
              style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 16px', minHeight: '44px', fontSize: '13.5px', color: '#051004', outline: 'none' }}
            >
              <option value="all">Toutes les notes</option>
              <option value="5">5 étoiles</option>
              <option value="4">4 étoiles</option>
              <option value="3">3 étoiles</option>
              <option value="2">2 étoiles</option>
              <option value="1">1 étoile</option>
            </select>
            <select
              value={reviewReplyFilter}
              onChange={(e) => setReviewReplyFilter(e.target.value)}
              className="min-w-[10rem] flex-1 sm:flex-initial"
              style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 16px', minHeight: '44px', fontSize: '13.5px', color: '#051004', outline: 'none' }}
            >
              <option value="all">Réponse : toutes</option>
              <option value="with_reply">Avec réponse</option>
              <option value="without_reply">Sans réponse</option>
            </select>
            <select
              value={reviewSort}
              onChange={(e) => setReviewSort(e.target.value)}
              className="min-w-[10rem] flex-1 sm:flex-initial"
              style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 16px', minHeight: '44px', fontSize: '13.5px', color: '#051004', outline: 'none' }}
            >
              <option value="date_desc">Plus récents</option>
              <option value="date_asc">Plus anciens</option>
              <option value="rating_desc">Note décroissante</option>
              <option value="rating_asc">Note croissante</option>
            </select>
            <select
              value={reviewDishFilter}
              onChange={(e) => setReviewDishFilter(e.target.value)}
              className="min-w-[10rem] flex-1 sm:flex-initial"
              style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 16px', minHeight: '44px', fontSize: '13.5px', color: '#051004', outline: 'none' }}
            >
              <option value="all">Tous les plats</option>
              {reviewDishNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        ) : activeTab === 'dishes' ? (
          <div className="flex flex-wrap gap-1" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '4px' }}>
            <button
              type="button"
              onClick={() => setFilterCategory('all')}
              style={{
                border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap',
                padding: '8px 16px', borderRadius: '999px',
                background: filterCategory === 'all' ? '#054625' : 'transparent',
                color: filterCategory === 'all' ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
              }}
            >
              Tous · {dishes.length}
            </button>
            {categories
              .filter((category) => category && typeof category === 'object' && category.id && category.name)
              .map((category) => {
                const active = String(filterCategory) === String(category.id);
                const count = dishes.filter((d) => (typeof d.category === 'object' ? d.category?.id : d.category_id) === category.id).length;
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setFilterCategory(category.id)}
                    style={{
                      border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap',
                      padding: '8px 16px', borderRadius: '999px',
                      background: active ? '#054625' : 'transparent',
                      color: active ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
                    }}
                  >
                    {category.name} · {count}
                  </button>
                );
              })}
          </div>
        ) : (
          <div className="sm:w-52">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full"
              style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 18px', minHeight: '44px', fontSize: '14px', color: '#051004', outline: 'none' }}
            >
              {activeTab === 'supplements' ? (
                <>
                  <option value="all">Tous les plats</option>
                  {dishes
                    .filter((dish) => dish && typeof dish === 'object' && dish.id && dish.name)
                    .map((dish) => (
                      <option key={dish.id} value={dish.id}>
                        {dish.name || 'Sans nom'}
                      </option>
                    ))}
                </>
              ) : (
                <>
                  <option value="all">Toutes les catégories</option>
                  {categories
                    .filter(
                      (category) =>
                        category && typeof category === 'object' && category.id && category.name
                    )
                    .map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name || 'Sans nom'}
                      </option>
                    ))}
                </>
              )}
            </select>
          </div>
        )}
      </div>

      {/* Barre d'actions groupées */}
      {activeTab === 'dishes' && selectedDishCount > 0 && (
        <div className="flex items-center gap-3 flex-wrap" style={{ background: '#051004', color: '#FCFBF5', borderRadius: '999px', padding: '8px 8px 8px 18px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {selectedDishCount} plat{selectedDishCount > 1 ? 's' : ''} sélectionné{selectedDishCount > 1 ? 's' : ''}
          </span>
          <button
            type="button"
            onClick={handleBulkPublish}
            disabled={bulkPublishing}
            style={{ border: 'none', cursor: 'pointer', background: '#FCFBF5', color: '#051004', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, minHeight: '34px', padding: '0 16px', borderRadius: '999px', whiteSpace: 'nowrap', opacity: bulkPublishing ? 0.6 : 1 }}
          >
            {bulkPublishing ? 'Publication…' : 'Publier'}
          </button>
          <button
            type="button"
            onClick={() => setSelectedDishIds({})}
            style={{ border: '1px solid rgba(252,251,245,0.4)', cursor: 'pointer', background: 'transparent', color: '#FCFBF5', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, minHeight: '34px', padding: '0 16px', borderRadius: '999px', whiteSpace: 'nowrap' }}
          >
            Désélectionner
          </button>
        </div>
      )}

      {/* Contenu */}
      {error && (
        <div style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '18px', padding: '14px 18px', fontSize: '14px' }}>
          {error?.message || error?.toString() || 'Une erreur est survenue'}
        </div>
      )}

      <div
        className={
          activeTab === 'reviews'
            ? 'divide-y divide-black/[0.05] overflow-hidden'
            : activeTab === 'dishes'
              ? 'flex flex-col gap-3'
              : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5'
        }
        style={
          activeTab === 'reviews' || activeTab === 'dishes'
            ? { background: activeTab === 'reviews' ? '#FCFBF5' : 'transparent', borderRadius: '28px' }
            : undefined
        }
      >
        {activeTab === 'dishes' ? (
          Array.isArray(filteredDishes) && filteredDishes.length > 0 ? (
            filteredDishes.map((dish, index) => renderDishCard(dish, index))
          ) : (
            <div className="col-span-full text-center py-16 text-namke-black">
              <Utensils className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucun plat trouvé</p>
            </div>
          )
        ) : activeTab === 'menus' ? (
          Array.isArray(filteredMenus) && filteredMenus.length > 0 ? (
            filteredMenus.map((menu, index) => renderMenuCard(menu, index))
          ) : (
            <div className="col-span-full text-center py-16 text-namke-black">
              <Utensils className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucun menu trouvé</p>
            </div>
          )
        ) : activeTab === 'supplements' ? (
          Array.isArray(filteredSupplements) && filteredSupplements.length > 0 ? (
            filteredSupplements.map((supplement, index) => renderSupplementCard(supplement, index))
          ) : (
            <div className="col-span-full text-center py-16 text-namke-black">
              <Utensils className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucun supplément trouvé</p>
            </div>
          )
        ) : (
          Array.isArray(filteredReviews) && filteredReviews.length > 0 ? (
            filteredReviews.map((review, index) => renderReviewCard(review, index))
          ) : (
            <div className="col-span-full text-center py-16 text-namke-black">
              <Star className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucun avis trouvé</p>
            </div>
          )
        )}
      </div>

      {/* Modal pour ajouter/modifier un plat */}
      <DishModal
        isOpen={showAddDishModal || !!selectedDish}
        onClose={() => {
          setShowAddDishModal(false);
          setSelectedDish(null);
        }}
        dish={selectedDish}
        onSuccess={selectedDish ? handleUpdateDish : handleCreateDish}
        categories={categories}
      />

      {/* Modal pour ajouter/modifier un menu */}
      <MenuModal
        isOpen={showAddMenuModal}
        onClose={() => {
          setShowAddMenuModal(false);
          setEditingMenu(null);
        }}
        menu={editingMenu}
        onSuccess={editingMenu ? (menuData) => handleUpdateMenu(editingMenu.id, menuData) : handleCreateMenu}
        availableDishes={dishes}
      />

      {/* Modal pour les détails du menu */}
      <MenuDetailsModal
        isOpen={showMenuDetails}
        onClose={() => {
          setShowMenuDetails(false);
          setSelectedMenu(null);
        }}
        menu={menus.find(m => m.id === selectedMenu?.id) || selectedMenu}
        allDishes={dishes}
        onMenuUpdate={loadData}
      />

      {/* Modal pour ajouter/modifier un supplément */}
      <SupplementModal
        isOpen={showAddSupplementModal || !!selectedSupplement}
        onClose={() => {
          setShowAddSupplementModal(false);
          setSelectedSupplement(null);
          setSelectedDishForSupplement(null);
        }}
        supplement={selectedSupplement}
        onSuccess={selectedSupplement ? handleUpdateSupplement : handleCreateSupplement}
        dishes={dishes}
        preselectedDishId={selectedDishForSupplement?.id}
        existingSupplements={supplements}
      />
    </div>
  );
};

export default RestaurantMenu;
